import { useState, useEffect } from "react";

// Detect env(keyboard-inset-height) support once at module load.
// CSS.supports checks syntax validity — modern iOS Safari and Chrome
// populate this variable when the virtual keyboard is open.
const supportsKeyboardEnv =
  typeof CSS !== "undefined" &&
  typeof CSS.supports === "function" &&
  CSS.supports("padding-bottom: env(keyboard-inset-height)");

function ManualEntryModal({
  isOpen,
  onClose,
  onSave,
  appTheme,
  isMobile,
  mode = "log",
  initialValues = null,
  isEditing = false,
}) {
  const [foodName, setFoodName] = useState("");
  const [calories, setCalories] = useState("");
  const [protein, setProtein] = useState("");
  const [carbs, setCarbs] = useState("");
  const [fats, setFats] = useState("");
  const [hasTriedSave, setHasTriedSave] = useState(false);
  // Only used in the visualViewport fallback path
  const [sheetOffset, setSheetOffset] = useState(0);

  // Prefill from initialValues when editing, otherwise reset
  useEffect(() => {
    if (isOpen) {
      setFoodName(initialValues?.name || "");
      setCalories(initialValues?.calories != null ? String(initialValues.calories) : "");
      setProtein(initialValues?.protein ? String(initialValues.protein) : "");
      setCarbs(initialValues?.carbs ? String(initialValues.carbs) : "");
      setFats(initialValues?.fats ? String(initialValues.fats) : "");
      setHasTriedSave(false);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen]);

  // Fallback keyboard avoidance via visualViewport (used only when
  // env(keyboard-inset-height) is not supported by the browser).
  // will-change: transform is set inline on the sheet element so the
  // browser promotes it to its own compositor layer, eliminating the
  // repaint flicker during scroll.
  useEffect(() => {
    if (!isOpen || supportsKeyboardEnv) return;

    function handleViewport() {
      const vv = window.visualViewport;
      if (!vv) return;
      setSheetOffset(Math.max(0, window.innerHeight - vv.height - vv.offsetTop));
    }

    window.visualViewport?.addEventListener("resize", handleViewport);
    window.visualViewport?.addEventListener("scroll", handleViewport);
    return () => {
      window.visualViewport?.removeEventListener("resize", handleViewport);
      window.visualViewport?.removeEventListener("scroll", handleViewport);
      setSheetOffset(0);
    };
  }, [isOpen]);

  if (!isOpen) return null;

  const isPlanMode = mode === "plan";
  const isValid = foodName.trim() && calories;

  function handleSave() {
    setHasTriedSave(true);
    if (!foodName.trim() || !calories) return;
    onSave({
      name: foodName.trim(),
      calories: Number(calories) || 0,
      protein: Number(protein) || 0,
      carbs: Number(carbs) || 0,
      fats: Number(fats) || 0,
      mode,
    });
    onClose();
  }

  const inputStyle = {
    width: "100%",
    boxSizing: "border-box",
    padding: "12px 14px",
    borderRadius: "12px",
    border: appTheme.cardBorder,
    background: appTheme.inputBackground,
    fontSize: "15px",
    color: appTheme.primaryText,
    outline: "none",
    fontFamily: "inherit",
  };
  const smallInput = { ...inputStyle, fontSize: "14px" };
  const sectionLabel = {
    fontSize: "12px",
    fontWeight: "700",
    color: appTheme.secondaryText,
    letterSpacing: "0.06em",
    textTransform: "uppercase",
    display: "block",
    marginBottom: "6px",
  };

  const nutritionFields = [
    { label: "Calories (kcal)", key: "calories", val: calories, set: setCalories, required: true },
    { label: "Protein (g)",     key: "protein",  val: protein,  set: setProtein  },
    { label: "Carbs (g)",       key: "carbs",    val: carbs,    set: setCarbs    },
    { label: "Fats (g)",        key: "fats",     val: fats,     set: setFats     },
  ];

  return (
    <>
      {/* Backdrop */}
      <div
        onClick={onClose}
        style={{
          position: "fixed", inset: 0, zIndex: 2000,
          background: "rgba(15,23,42,0.45)",
          backdropFilter: "blur(4px)", WebkitBackdropFilter: "blur(4px)",
        }}
      />

      {/* Sheet
          Primary:  paddingBottom env(keyboard-inset-height) lets Safari
                    handle the keyboard gap natively — no JS animation.
          Fallback: translateY from visualViewport for older browsers;
                    will-change: transform keeps it on a compositor layer
                    so scroll doesn't trigger a repaint flicker.
          overflowY: hidden here — only the inner form div scrolls.       */}
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          position: "fixed",
          bottom: 0, left: 0, right: 0,
          zIndex: 2001,
          maxWidth: "560px",
          margin: "0 auto",
          borderRadius: "24px 24px 0 0",
          background: appTheme.pageBackground,
          boxShadow: "0 -8px 40px rgba(15,23,42,0.14)",
          animation: "settingsSheetIn 0.28s cubic-bezier(0.22, 1, 0.36, 1)",
          display: "flex",
          flexDirection: "column",
          overflowY: "hidden",
          willChange: "transform",
          ...(supportsKeyboardEnv
            ? { paddingBottom: "env(keyboard-inset-height, 0px)" }
            : {
                transform: `translateY(-${sheetOffset}px)`,
                transition: sheetOffset > 0 ? "transform 0.15s ease" : "none",
              }
          ),
        }}
      >
        {/* Drag handle */}
        <div style={{ display: "flex", justifyContent: "center", paddingTop: "10px", paddingBottom: "4px", flexShrink: 0 }}>
          <div style={{ width: "36px", height: "4px", borderRadius: "999px", background: appTheme.cardBorder.replace("1px solid ", "") }} />
        </div>

        {/* Header */}
        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", padding: "10px 20px", flexShrink: 0 }}>
          <h2 style={{ margin: 0, fontSize: "17px", fontWeight: "500", fontFamily: appTheme.fontHeading, color: appTheme.primaryText }}>
            {mode === "ate-different" ? "What did you have instead?" : isPlanMode ? "Plan a Snack" : isEditing ? "Edit Snack" : "Log a Snack"}
          </h2>
          <button
            onClick={onClose}
            style={{ width: "30px", height: "30px", borderRadius: "999px", border: appTheme.pillBorder, background: appTheme.pillBackground, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}
          >
            <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
              <path d="M1 1l10 10M11 1L1 11" stroke={appTheme.secondaryText} strokeWidth="1.8" strokeLinecap="round"/>
            </svg>
          </button>
        </div>

        {/* Form — this div scrolls if content overflows */}
        <div className="modal-scroll-hidden" style={{ overflowY: "auto", flex: 1, padding: "8px 20px 16px", display: "flex", flexDirection: "column", gap: "20px" }}>

          {/* Food name */}
          <div>
            <label style={sectionLabel}>Food name *</label>
            <input
              autoFocus
              value={foodName}
              onChange={(e) => setFoodName(e.target.value)}
              placeholder="e.g. Protein bar, handful of nuts..."
              style={inputStyle}
            />
            {hasTriedSave && !foodName.trim() && (
              <div style={{ color: "#E6544C", fontSize: "12px", marginTop: "4px" }}>* Required</div>
            )}
          </div>

          {/* Nutrition */}
          <div>
            <div style={{ fontSize: "12px", fontWeight: "700", color: appTheme.secondaryText, letterSpacing: "0.06em", textTransform: "uppercase", marginBottom: "8px" }}>
              Nutrition{" "}
              <span style={{ fontWeight: "400", textTransform: "none", letterSpacing: 0 }}>
                — calories required
              </span>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "8px" }}>
              {nutritionFields.map(({ label, key, val, set, required }) => (
                <div key={key}>
                  <div style={{ fontSize: "11px", color: appTheme.secondaryText, marginBottom: "4px" }}>
                    {label}{required ? " *" : ""}
                  </div>
                  <input
                    type="number"
                    inputMode="numeric"
                    min="0"
                    value={val}
                    onChange={(e) => set(e.target.value)}
                    placeholder="—"
                    style={required && hasTriedSave && !val
                      ? { ...smallInput, border: "1px solid rgba(230,84,76,0.6)" }
                      : smallInput
                    }
                  />
                </div>
              ))}
            </div>
          </div>

        </div>

        {/* CTA */}
        <div style={{ padding: `12px 20px ${isMobile ? "32px" : "20px"}`, borderTop: "1px solid rgba(0,0,0,0.06)", background: appTheme.pageBackground, flexShrink: 0 }}>
          <button
            onClick={handleSave}
            style={{
              width: "100%", padding: "14px", borderRadius: "14px", border: "none",
              background: isValid ? appTheme.accentColor : appTheme.pillBackground,
              color: isValid ? "#ffffff" : appTheme.secondaryText,
              fontSize: "15px", fontWeight: "700",
              cursor: isValid ? "pointer" : "not-allowed",
              opacity: isValid ? 1 : 0.55,
              transition: "all 0.18s ease",
            }}
          >
            {mode === "ate-different" ? "Save" : isPlanMode ? "Add to Plan" : isEditing ? "Update Snack" : "Log Snack"}
          </button>
        </div>
      </div>
    </>
  );
}

export default ManualEntryModal;
